import GatewayType from '../../../gateway/pages/GatewayType'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import Loading from '../../../gateway/layout/Loading'
import SharePointSettingsService from '../service/SharePointSettingsService'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AppContext from '../../../gateway/Authentication/AppContext'
import AuthService from '../../../gateway/Authentication/AuthService'
import { formatBooleanColumn } from '../../../gateway/utils/DatatableUtil'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'

const SharePointSettings: GatewayType = () => {
    const { t } = useTranslation()
    const [sharePointCustomFields, setSharePointCustomFields] = useState({} as any)
    const [loading, setLoading] = useState(true)
    const { userPermissions } = useContext(AppContext)
    const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

    useEffect(() => {
        SharePointSettingsService.getAll(setSharePointCustomFields).finally(() => setLoading(false))
    }, [])

    const columns = useMemo(() => {
        return [
            {
                dataField: 'id',
                sort: false,
                hidden: true,
            },
            {
                text: t('sharePointSettings.name'),
                dataField: 'name',
                sort: true,
            },
            {
                text: t('sharePointSettings.internalName'),
                dataField: 'internalName',
                sort: true,
            },
            {
                text: t('sharePointSettings.encrypt'),
                dataField: 'encrypt',
                formatter: (cell: any) => formatBooleanColumn(cell),
                sort: true,
            },
            {
                hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.edit),
                dataField: 'df1',
                isDummyField: true,
                text: t('action'),
                headerAlign: 'center',
                formatter: (cellData: any, row: any) => {
                    return (
                        <>
                            <ProtectedButton
                                permissions={Authorization.settings.o365Setting.edit}
                                variant='link'
                                className='text-decoration-none pr-2'
                                tooltip={t('edit')}
                                onClick={() => edit(t, editDialogPropsSetter, setSharePointCustomFields, row)}>
                                <FontAwesomeIcon icon={faEdit} />
                            </ProtectedButton>
                            <ProtectedButton
                                permissions={Authorization.settings.o365Setting.edit}
                                variant='link'
                                className='text-danger text-link text-decoration-none'
                                tooltip={t('delete')}
                                onClick={() => deleteCustomField(t, confirmDialogPropsSetter, setSharePointCustomFields, row)}>
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </ProtectedButton>

                        </>
                    )
                },
            },
        ]
    }, [t, editDialogPropsSetter, confirmDialogPropsSetter, userPermissions])

    return (
        <Loading isLoading={loading}>
            <DataTable
                data={sharePointCustomFields}
                columns={columns}
                caption={t('sharePointSettings.text')}
                captionButtons={
                    [
                        {
                            onClick: () => create(t, editDialogPropsSetter, setSharePointCustomFields),
                            text: t('new'),
                            permissions: Authorization.settings.o365Setting.edit,
                        }
                    ]
                }
            />
        </Loading>
    )
}


function create(t: TFunction, editDialogPropsSetter: any, setRestProtectionConfigurations: React.Dispatch<any>) {
    const editDialogProps: EditDialogProps = {
        show: true,
        header: t('sharePointSettings.createHeader'),
        onSubmit: SharePointSettingsService.create,
        onSubmitExtraArgs: [t, setRestProtectionConfigurations],
        data: [
            {
                id: 'name',
                type: 'text',
                label: t('sharePointSettings.name'),
                placeholder: t('sharePointSettings.name'),
                required: true,
            },
            {
                id: 'internalName',
                type: 'text',
                label: t('sharePointSettings.internalName'),
                placeholder: t('sharePointSettings.internalName'),
                required: true,
            },
            {
                groupLabel: t('sharePointSettings.encrypt'),
                groupName: 'encrypt',
                type: 'switch',
                checkboxes: [
                    {
                        id: 'encrypt'
                    },
                ],
            }
        ]
    }
    editDialogPropsSetter(editDialogProps)
}

function edit(t: TFunction, editDialogPropsSetter: any, setSharePointCustomFields: React.Dispatch<any>, row: any) {
    const editDialogProps: EditDialogProps = {
        show: true,
        header: t('sharePointSettings.editHeader'),
        onSubmit: SharePointSettingsService.edit,
        onSubmitExtraArgs: [t, setSharePointCustomFields],
        data: [
            {
                id: 'id',
                type: 'hidden',
                value: row.id,
                render: false,
            },
            {
                id: 'name',
                type: 'text',
                label: t('sharePointSettings.name'),
                value: row.name,
                required: true,
            },
            {
                id: 'internalName',
                type: 'text',
                label: t('sharePointSettings.internalName'),
                value: row.internalName,
                required: true,
            },
            {
                groupLabel: t('sharePointSettings.encrypt'),
                groupName: 'encrypt',
                type: 'switch',
                checkboxes: [
                    {
                        id: 'encrypt',
                        value: row.encrypt
                    },
                ],
            },

        ],
        labelGridSize: 4,
    }
    editDialogPropsSetter(editDialogProps)
}

function deleteCustomField(t: TFunction, confirmDialogPropsSetter: React.Dispatch<any>, setRestProtectionConfigurations: React.Dispatch<any>, row: any) {
    const deleteDialogProps: ConfirmDialogProps = {
        show: true,
        header: t('sharePointSettings.deleteHeader'),
        body: t('sharePointSettings.deleteFieldBody', { path: row.name }),
        submitText: t('yes'),
        onSubmit: SharePointSettingsService.delete,
        onSubmitExtraArgs: [row, t, setRestProtectionConfigurations],
        cancelText: t('no'),
    }
    confirmDialogPropsSetter(deleteDialogProps)
}

export default SharePointSettings
